/**
 * Filtro por jerarquía de productos (R-35, §90): ATC a cualquier profundidad,
 * agrupación homogénea, conjunto SPR, principio activo, laboratorio titular o nº de registro.
 * Devuelve el valor como `tipo:valor` para el parámetro `hier` de la API.
 */
import { useEffect, useState } from "react";
import { useT } from "../i18n";
import SuggestInput from "./SuggestInput";

export const HIER_KINDS = ["atc", "ah", "spr", "pa", "lab", "nregistro"] as const;
export type HierKind = typeof HIER_KINDS[number];

export function parseHier(v: string): { kind: HierKind; value: string } | null {
  const i = v.indexOf(":");
  if (i < 0) return null;
  const kind = v.slice(0, i) as HierKind;
  return HIER_KINDS.includes(kind) ? { kind, value: v.slice(i + 1) } : null;
}

export default function HierarchyFilter({ value, onChange, style }: {
  value: string; onChange: (hier: string) => void; style?: React.CSSProperties;
}) {
  const t = useT();
  const parsed = parseHier(value);
  const [kind, setKind] = useState<HierKind>(parsed?.kind ?? "atc");
  const [text, setText] = useState(parsed?.value ?? "");
  // El valor se emite con retardo para no disparar una petición de lista por pulsación;
  // las sugerencias las pide SuggestInput (desde el primer clic, sin escribir).
  useEffect(() => {
    const v = text.trim();
    const h = setTimeout(() => onChange(v ? `${kind}:${v}` : ""), 350);
    return () => clearTimeout(h);
  }, [kind, text]);

  const base: React.CSSProperties = { background:"var(--clr-input-bg)", border:"1px solid var(--clr-border)", borderRadius:999, color:"var(--clr-text)", padding:"0.5rem 0.85rem", fontSize:"0.82rem", outline:"none" };

  return (
    <span style={{ display:"inline-flex", gap:6, alignItems:"center", ...style }}>
      <select value={kind} onChange={e => { setKind(e.target.value as HierKind); setText(""); }} style={{ ...base, width:"auto" }} title={t("hierarchy.title")}>
        {HIER_KINDS.map(k => <option key={k} value={k}>{t(`hierarchy.kind_${k}`)}</option>)}
      </select>
      <SuggestInput kind={kind} value={text} onChange={setText} placeholder={t(`hierarchy.placeholder_${kind}`)} style={{ ...base, minWidth:170 }} />
      {text && <button onClick={() => setText("")} title={t("hierarchy.clear")} style={{ background:"transparent", border:0, color:"var(--clr-text4)", cursor:"pointer", fontSize:"0.9rem" }}>×</button>}
    </span>
  );
}
